<template>
  <Transition
    leave-active-class="transition duration-200 ease-in motion-reduce:transition-none"
    leave-to-class="opacity-0 -translate-y-1">
    <div v-if="store.visible" ref="root">
      <HellobarView :bar="store.currentBar" :preview="store.previewing"
        @dismiss="store.dismiss()" @cta="followCta" />
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch, inject, onMounted, onUnmounted, nextTick } from 'vue'
import { routerKey } from 'vue-router'
import { useHellobarStore, PREVIEW_STORAGE_KEY } from 'stores/hellobar.js'
import HellobarView from 'controllers/vue_components/hellobar/HellobarView'

const CATCH_ALL_PATH = '/:pathMatch(.*)*'

const store = useHellobarStore()
// Injected softly: pages mounted without the router must not warn.
const router = inject(routerKey, null)
const root = ref(null)
let observer = null

// Hydrate before the first render so the bar paints with the page.
store.hydrate()

// The CTA must not be a Turbo visit (see HellobarView). Pages Vue Router owns
// get an in-app navigation; anything else gets a full page load, after the
// click is recorded so the unload cannot cancel that request. Modified clicks
// (new tab, new window) are left to the browser.
async function followCta(event) {
  const url = store.currentBar?.cta?.url
  if (!url) return

  if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) {
    store.recordClick()
    return
  }
  event?.preventDefault()

  const target = router?.resolve(url)
  const routed = target && target.matched.length > 0 && !target.matched.some((record) => record.path === CATCH_ALL_PATH)

  if (routed) {
    store.recordClick()
    router.push(url)
    return
  }

  await store.recordClick()
  window.location.assign(url)
}

// Full height pages size themselves with h-app-screen, which subtracts this
// variable. Keeping it equal to the bar's real height (it wraps on mobile)
// stops a visible bar from pushing those pages past the viewport.
function setHeight(px) {
  document.documentElement.style.setProperty('--hellobar-height', `${px}px`)
}

function observe() {
  observer?.disconnect()
  if (!root.value) return setHeight(0)
  setHeight(root.value.offsetHeight)
  if (typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(() => {
    if (root.value) setHeight(root.value.offsetHeight)
  })
  observer.observe(root.value)
}

watch(() => store.visible, async (visible) => {
  if (!visible) {
    observer?.disconnect()
    setHeight(0)
    return
  }
  await nextTick()
  observe()
})

// The admin page writes the draft in another tab; follow it live.
function onStorage(event) {
  if (event.key === PREVIEW_STORAGE_KEY) store.refreshPreview()
}

onMounted(() => {
  observe()
  if (store.visible) store.recordImpression()
  window.addEventListener('storage', onStorage)
})

onUnmounted(() => {
  observer?.disconnect()
  setHeight(0)
  window.removeEventListener('storage', onStorage)
})
</script>
